/**
 * Simplified Security Hook
 * 
 * This hook provides a streamlined interface to the SecurityService,
 * reducing the complexity of security checks throughout the application.
 * 
 * Key improvements:
 * - Single source of truth for all security logic
 * - Reduced Firebase function calls
 * - Consistent error handling
 * - Better performance through caching
 */

import { useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useOrganization } from '../contexts/OrganizationContext';
import SecurityService, { ROLES } from '../services/SecurityService';

export const useSimplifiedSecurity = () => {
  const { currentUser } = useAuth();
  const { organizationId, userRole, isApproved } = useOrganization();

  // Memoized permission checks to avoid recalculation
  const permissions = useMemo(() => {
    if (!userRole) return {};
    return SecurityService.getRolePermissions(userRole);
  }, [userRole]);

  // Check if user has permission for an operation
  const hasPermission = useCallback((operation) => {
    return SecurityService.hasPermission(operation, userRole);
  }, [userRole]);

  // Check if user can upload a specific file type
  const canUploadFileType = useCallback((fileType) => {
    return SecurityService.canUploadFileType(fileType, userRole);
  }, [userRole]);

  // Check if user can view bills (with organization setting)
  const canViewBills = useCallback((allowInventoryManagerBillAccess = true) => {
    return SecurityService.canViewBills(userRole, allowInventoryManagerBillAccess);
  }, [userRole]);

  // Check if user can edit a specific order
  const canEditOrder = useCallback((orderCreatedBy, orderAssignedTo) => {
    return SecurityService.canEditOrder(userRole, orderCreatedBy, orderAssignedTo, currentUser?.uid);
  }, [userRole, currentUser?.uid]);

  // Check if user can cancel a specific order
  const canCancelOrder = useCallback((orderCreatedBy, orderAssignedTo) => {
    return SecurityService.canCancelOrder(userRole, orderCreatedBy, orderAssignedTo, currentUser?.uid);
  }, [userRole, currentUser?.uid]);

  // Validate access with detailed error message
  const validateAccess = useCallback((operation, context = {}) => {
    return SecurityService.validateAccess(operation, userRole, context);
  }, [userRole]);

  // Check if user has any of the required roles
  const hasAnyRole = useCallback((requiredRoles) => {
    return SecurityService.hasAnyRole(requiredRoles, userRole);
  }, [userRole]);

  // Get required roles for a file type
  const getRequiredRolesForFileType = useCallback((fileType) => {
    return SecurityService.getRequiredRolesForFileType(fileType);
  }, []);

  // Check if user is authenticated and has organization access
  const isAuthenticated = useMemo(() => {
    return !!(currentUser && organizationId && isApproved);
  }, [currentUser, organizationId, isApproved]);

  // Check if user is a business owner
  const isBusinessOwner = useMemo(() => {
    return userRole === ROLES.BUSINESS_OWNER;
  }, [userRole]);

  // Check if user can manage inventory
  const canManageInventory = useMemo(() => {
    return hasPermission('CAN_MANAGE_PRODUCTS') && hasPermission('CAN_MANAGE_STOCK');
  }, [hasPermission]);

  // Check if user can view inventory
  const canViewInventory = useMemo(() => {
    return hasPermission('CAN_VIEW_PRODUCTS') && hasPermission('CAN_VIEW_STOCK');
  }, [hasPermission]);

  // Check if user can manage orders
  const canManageOrders = useMemo(() => {
    return hasPermission('CAN_CREATE_ORDERS') || hasPermission('CAN_EDIT_ORDERS') || hasPermission('CAN_APPROVE_ORDERS');
  }, [hasPermission]);

  // Check if user can view all orders
  const canViewAllOrders = useMemo(() => {
    return hasPermission('CAN_VIEW_ALL_ORDERS');
  }, [hasPermission]);

  // Check if user can manage users
  const canManageUsers = useMemo(() => {
    return hasPermission('CAN_MANAGE_USERS');
  }, [hasPermission]);

  return {
    // Basic state
    isAuthenticated,
    isBusinessOwner,
    userRole,
    organizationId,
    
    // Permission checks
    hasPermission,
    hasAnyRole,
    validateAccess,
    
  // File operations
  canUploadFileType,
  getRequiredRolesForFileType,
  canViewBills: hasPermission('CAN_VIEW_BILLS'),
  canUploadBills: hasPermission('CAN_UPLOAD_BILLS'),
  canUploadChallans: hasPermission('CAN_UPLOAD_CHALLANS'),
  canUploadBiltys: hasPermission('CAN_UPLOAD_BILTYS'),
  canUploadProducts: hasPermission('CAN_UPLOAD_PRODUCTS'),
  canViewChallans: hasPermission('CAN_VIEW_CHALLANS'),
  canViewBiltys: hasPermission('CAN_VIEW_BILTYS'),
  canViewProducts: hasPermission('CAN_VIEW_PRODUCTS'),
    
  // Order operations
  canEditOrder,
  canCancelOrder,
  canManageOrders,
  canViewAllOrders,
  canApproveOrders: hasPermission('CAN_APPROVE_ORDERS'),
  canDispatchOrders: hasPermission('CAN_DISPATCH_ORDERS'),
    
    // Inventory operations
    canManageInventory,
    canViewInventory,
    
    // User management
    canManageUsers,

    // Payments & Dues
    canManageReceivables: hasPermission('CAN_MANAGE_RECEIVABLES'),
    canViewReceivables: hasPermission('CAN_VIEW_RECEIVABLES'),
    
    // All permissions (for debugging)
    permissions
  };
};
